import { Suspense, lazy, useEffect } from 'react'
import { Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { applyTheme, readTheme, type Surface } from './lib/theme.js'
import { AppProvider } from './lib/app.js'
import { Home } from './routes/Home.js'
import { ErrorBoundary } from './components/ErrorBoundary.js'

/**
 * Routes are lazy so each surface only downloads what it can actually run.
 *
 * The wall tablet only ever renders /board. Making it fetch and parse the phone
 * screens and the desktop planner costs real seconds on a Cortex-A55 at cold
 * start, for code it will never execute (docs/02 §6).
 */
const BoardScreen = lazy(() => import('./routes/BoardScreen.js').then(m => ({ default: m.BoardScreen })))
const Planner = lazy(() => import('./routes/Planner.js').then(m => ({ default: m.Planner })))
const Phone = lazy(() => import('./routes/Phone.js').then(m => ({ default: m.Phone })))
const SettingsScreen = lazy(() => import('./settings/SettingsScreen.js').then(m => ({ default: m.SettingsScreen })))

/** Which surface a route runs on. Drives type scale, theming and interaction. */
function surfaceFor(pathname: string): Surface {
  if (pathname.startsWith('/board')) return 'wall'
  if (pathname.startsWith('/app')) return 'phone'
  return 'desk'
}

function Loading() {
  return (
    <div style={{
      minHeight: '100dvh', display: 'grid', placeItems: 'center',
      background: 'var(--bg)', color: 'var(--text-subtle)',
    }} role="status" aria-live="polite">
      Loading…
    </div>
  )
}

export function App() {
  const { pathname } = useLocation()
  const surface = surfaceFor(pathname)

  useEffect(() => { applyTheme(readTheme(), surface) }, [surface])

  return (
    <ErrorBoundary area={surface === 'wall' ? 'the board' : 'the app'}>
      <AppProvider>
        <Suspense fallback={<Loading />}>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/board/*" element={<BoardScreen />} />
          <Route path="/plan/*" element={<Planner />} />
          <Route path="/app/*" element={<Phone />} />
          <Route path="/settings" element={<SettingsScreen />} />
          <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </AppProvider>
    </ErrorBoundary>
  )
}
